import type { ComponentProps } from 'react';
import React from 'react';
import { hasV3Header, ItandiHeader } from '@itandi/brand-ui/ItandiHeader';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import Link from 'next/link';
import useItandiAccount from '~/hooks/useItandiAccount';
import { logout as logoutStaff } from '~/utils/api/Staff';
import { logout as logoutStore } from '~/utils/api/ItandiAccountSession';
import { PATH as ADMIN_COMPANY_SETTINGS_PATH } from '~/pages/admin';
import { PATH as ADMIN_VENDOR_COMPANIES_PATH } from '~/pages/admin/vendor_companies';
import { PATH as HOME_PATH } from '~/pages/index';
import { PATH as DEALS_PATH } from '~/pages/deals';
import { PATH as TODOS_PATH } from '~/pages/todos';
import { PATH as MESSAGES_PATH } from '~/pages/messages';
import { PATH as SETTINGS_PATH } from '~/pages/settings';
import { PATH as FORBIDDEN_PATH } from '~/pages/403';
import { isSuccess } from '~/utils/api/api_handler';
import { destroy as logoutIaUser } from '~/utils/api/ItandiUserSessions';
import { canOpenSetting } from '~/models/Staff';

const isActivePage = (currentPath: string, targetPath: string): boolean => {
  if (targetPath === '/admin') {
    return currentPath === '/admin';
  }
  if (targetPath === '/') {
    return currentPath === '/';
  }
  return currentPath.includes(targetPath);
};

export const ItandiHeaderContainer: React.FC = () => {
  const router = useRouter();
  const { data: currentAccount, mutate: mutateAccount } =
    useItandiAccount().currentAccount();
  const handleChangeStaff = React.useCallback(async () => {
    if (currentAccount?.company?.useIaStaffLogin) {
      const response = await logoutIaUser();
      if (isSuccess(response) && response.data) {
        window.location.replace(response.data.url);
      }
    } else {
      await logoutStaff();
      await mutateAccount?.();
    }
  }, [currentAccount?.company?.useIaStaffLogin, mutateAccount]);
  const handleLogout = React.useCallback(async () => {
    const response = await logoutStore();
    if (isSuccess(response) && response.data) {
      window.location.replace(response.data.url);
      return;
    }
    toast.error('エラーが発生しました');
  }, []);
  const itandiAccount = React.useMemo(() => {
    if (!currentAccount?.company?.name || !currentAccount?.store?.name) {
      return undefined;
    }
    return {
      companyName: currentAccount.company.name,
      storeName: currentAccount.store.name
    };
  }, [currentAccount]);
  const staffAccount = React.useMemo(() => {
    if (!currentAccount?.staff) {
      return undefined;
    }
    return {
      name: currentAccount.staff.name
    };
  }, [currentAccount]);
  const isShowAdminHeader = React.useMemo((): boolean => {
    if (currentAccount === null || !currentAccount.isAdmin) {
      return false;
    }
    if (router.route.includes('/admin')) {
      return true;
    }
    return currentAccount.company === null;
  }, [currentAccount, router]);
  if (currentAccount === null) {
    return null;
  }

  const adminMenus = [
    {
      active: isActivePage(router.pathname, ADMIN_COMPANY_SETTINGS_PATH),
      as: Link,
      kind: 'link',
      href: ADMIN_COMPANY_SETTINGS_PATH,
      label: '管理会社一覧'
    },
    {
      active: isActivePage(router.pathname, ADMIN_VENDOR_COMPANIES_PATH),
      as: Link,
      kind: 'link',
      href: ADMIN_VENDOR_COMPANIES_PATH,
      label: '施工会社一覧'
    }
  ] as const satisfies ComponentProps<typeof ItandiHeader>['menus'];

  const defaultMenus = [
    {
      active: isActivePage(router.pathname, HOME_PATH),
      as: Link,
      kind: 'link',
      href: HOME_PATH,
      label: hasV3Header(currentAccount.productParams)
        ? 'ダッシュボード'
        : 'HOME'
    },
    {
      active: isActivePage(router.pathname, DEALS_PATH),
      as: Link,
      kind: 'link',
      href: DEALS_PATH,
      label: '案件一覧'
    },
    {
      active: isActivePage(router.pathname, TODOS_PATH),
      as: Link,
      kind: 'link',
      href: TODOS_PATH,
      label: 'TODO一覧'
    },
    {
      active: isActivePage(router.pathname, MESSAGES_PATH),
      as: Link,
      kind: 'link',
      href: MESSAGES_PATH,
      label: 'チャット'
    },
    {
      active: isActivePage(router.pathname, SETTINGS_PATH),
      as: Link,
      kind: 'link',
      href: canOpenSetting(currentAccount) ? SETTINGS_PATH : FORBIDDEN_PATH,
      label: '設定'
    },
    currentAccount.isAdmin
      ? {
          active: false,
          as: Link,
          kind: 'link' as const,
          href: ADMIN_COMPANY_SETTINGS_PATH,
          label: 'Admin'
        }
      : null
  ] as const satisfies ComponentProps<typeof ItandiHeader>['menus'];

  return (
    <ItandiHeader
      changeStaff={handleChangeStaff}
      isAdmin={currentAccount.isAdmin}
      itandiAccount={itandiAccount}
      logout={handleLogout}
      menus={isShowAdminHeader ? adminMenus : defaultMenus}
      productCode="nsk"
      productMenus={currentAccount.productParams}
      staffAccount={staffAccount}
    />
  );
};
